import { Fragment, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CaretDown, CaretRight, FileText, Repeat, UploadSimple, X, Image as ImageIcon, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useInvoiceViewer } from "@/components/InvoiceViewer";
import { useSort, sortRows, SortHeader } from "@/components/SortHeader";

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const day = (d) => (d || "").slice(0, 10);
const kindLabel = { cost: "Cost", creator_payment: "Payment", creator: "GST invoice" };
const displayName = (r) => (r.kind === "cost" ? r.creator_username || "Additional cost" : `@${r.creator_username || "—"}`);

const fmtDateTime = (d, t) => {
  if (!d) return "—";
  const sameDay = t && new Date(t).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) === day(d);
  const time = sameDay ? new Date(t).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true }) : "";
  return `${fmtDate(d)}${time ? `, ${time}` : ""}`;
};
const fmtMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};

export function groupGstRows(rows) {
  const map = new Map();
  const out = [];
  for (const r of rows) {
    const num = (r.invoice_number || "").trim();
    if (!num) { out.push({ key: `${r.kind}-${r.id}`, rows: [r] }); continue; }
    const key = `${num}::${r.kind === "cost" ? "c" : "p"}::${r.creator_username || ""}`;
    if (!map.has(key)) { const g = { key, rows: [] }; map.set(key, g); out.push(g); }
    map.get(key).rows.push(r);
  }
  return out.map((g) => {
    const f = g.rows[0];
    return {
      ...g, first: f, grouped: !!(f.invoice_number || "").trim(),
      invoice_number: f.invoice_number || "",
      invoice_date: g.rows.map((r) => r.invoice_date || "").filter(Boolean).sort()[0] || "",
      latest_paid_at: g.rows.map((r) => r.latest_paid_at || "").sort().pop() || "",
      total_gst: g.rows.reduce((s, r) => s + Number(r.gst_pending || 0), 0),
      total_cost: g.rows.reduce((s, r) => s + Number(r.final_cost || 0), 0),
    };
  });
}

function MonthFilter({ months, value, onChange }) {
  return (
    <div className="inline-flex items-center gap-2" data-testid="gst-month-filter">
      <CalendarBlank size={14} className="text-slate-500" />
      <span className="text-[10px] uppercase tracking-wider text-slate-500">Invoice month</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 w-[180px] text-sm font-semibold border-slate-300" data-testid="gst-month-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="gst-month-option-all">All months</SelectItem>
          {months.map((m) => <SelectItem key={m} value={m} data-testid={`gst-month-option-${m}`}>{fmtMonth(m)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function NameCell({ r, count }) {
  return (
    <div>
      <span>
        {displayName(r)}
        <span className="ml-1.5 text-[10px] rounded-full px-1.5 py-0.5 font-semibold border bg-slate-50 text-slate-600 border-slate-200">
          {count > 1 ? `${count} payments` : kindLabel[r.kind]}
        </span>
      </span>
      {r.cost_poc_name && <div className="text-[11px] text-slate-500 mt-0.5 font-normal">{r.cost_poc_name}</div>}
      {r.legal_name && <div className="text-[11px] text-slate-500 mt-0.5 font-normal" data-testid={`gst-legal-name-${r.id}`}>{r.legal_name}</div>}
    </div>
  );
}

function InvoiceCell({ r }) {
  const { openInvoice } = useInvoiceViewer();
  if (!r.invoice_file_id) return <span className="text-xs text-slate-400">{r.invoice_type || "—"}</span>;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); openInvoice(r.invoice_file_id, r.invoice_filename); }}
      className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 text-xs" data-testid={`gst-invoice-link-${r.id}`}>
      <FileText size={11} /> {r.invoice_type || "Invoice"}
    </button>
  );
}

function GroupRow({ g, isAdmin, open, onToggle, onMark }) {
  const r = g.first;
  const multi = g.rows.length > 1;
  return (
    <TableRow className={`hover:bg-slate-50/60 ${multi ? "cursor-pointer" : ""}`} onClick={multi ? onToggle : undefined} data-testid={`gst-group-${g.key}`}>
      <TableCell className="w-8 px-2">
        {multi && (
          <button type="button" className="text-slate-500" data-testid={`gst-group-toggle-${g.key}`} aria-label="Toggle payments">
            {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
          </button>
        )}
      </TableCell>
      <TableCell className="font-medium text-slate-900"><NameCell r={r} count={g.rows.length} /></TableCell>
      <TableCell className="text-xs text-slate-700">
        <div className="font-semibold text-slate-900">{r.brand_name || "—"}</div>
        {r.campaign_name && <div className="text-[11px] text-slate-500">{r.campaign_name}</div>}
      </TableCell>
      <TableCell className="text-right font-mono text-xs text-slate-700">{fmtCur(g.total_cost)}</TableCell>
      <TableCell className="text-right font-mono text-xs font-semibold text-indigo-700" data-testid={`gst-group-total-${g.key}`}>{fmtCur(g.total_gst)}</TableCell>
      <TableCell><InvoiceCell r={r} /></TableCell>
      <TableCell className="text-xs text-slate-500" data-testid={`gst-invoice-date-${r.id}`}>{fmtDate(g.invoice_date)}</TableCell>
      <TableCell className="font-mono text-[11px] text-slate-700" data-testid={`gst-invoice-number-${r.id}`}>{g.invoice_number || <span className="text-slate-400">—</span>}</TableCell>
      <TableCell className="text-xs text-slate-500">{fmtDate(g.latest_paid_at)}</TableCell>
      {isAdmin && (
        <TableCell className="text-right">
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onMark(g); }} className="border-slate-300 h-7 text-xs" data-testid={`mark-reimbursed-${r.id}`}>
            <Repeat size={12} className="mr-1" /> Mark reimbursed
          </Button>
        </TableCell>
      )}
    </TableRow>
  );
}

function ChildRow({ r, isAdmin }) {
  return (
    <TableRow className="bg-slate-50/70 text-xs" data-testid={`gst-row-${r.id}`}>
      <TableCell />
      <TableCell className="pl-6 text-slate-600">{kindLabel[r.kind]} · {displayName(r)}</TableCell>
      <TableCell />
      <TableCell className="text-right font-mono text-slate-600">{fmtCur(r.final_cost)}</TableCell>
      <TableCell className="text-right font-mono text-indigo-700" data-testid={`gst-amount-${r.id}`}>{fmtCur(r.gst_pending)}</TableCell>
      <TableCell /><TableCell /><TableCell />
      <TableCell className="text-slate-500">{fmtDate(r.latest_paid_at)}</TableCell>
      {isAdmin && <TableCell />}
    </TableRow>
  );
}

export function GstPendingSection({ rows, loading, isAdmin, onChanged }) {
  const [month, setMonth] = useState("all");
  const [openKeys, setOpenKeys] = useState({});
  const [marking, setMarking] = useState(null);
  const [sort, toggleSort] = useSort("invoice_date");
  const allGroups = useMemo(() => groupGstRows(rows), [rows]);
  const months = useMemo(() => [...new Set(allGroups.map((g) => day(g.invoice_date).slice(0, 7)).filter(Boolean))].sort().reverse(), [allGroups]);
  const groups = useMemo(() => sortRows(
    allGroups.filter((g) => month === "all" || day(g.invoice_date).slice(0, 7) === month),
    (g) => g[sort.key], sort.dir,
  ), [allGroups, month, sort]);
  const hasFilter = month !== "all";
  const cols = isAdmin ? 10 : 9;

  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">GST reimbursement pending</div>
      <Card className="p-3 sm:p-4 border border-slate-200 shadow-none mb-3" data-testid="gst-filters">
        <div className="flex flex-wrap items-center gap-3">
          <MonthFilter months={months} value={month} onChange={setMonth} />
          {hasFilter && (
            <Button size="sm" variant="ghost" className="h-9 text-xs text-slate-600" onClick={() => setMonth("all")} data-testid="gst-filter-clear">
              <X size={12} className="mr-1" /> Show all months
            </Button>
          )}
          <div className="ml-auto text-right text-xs" data-testid="gst-filter-summary">
            <div className="text-[10px] uppercase tracking-wider text-indigo-700">GST pending{hasFilter ? ` · ${fmtMonth(month)}` : ""}</div>
            <div className="font-mono font-semibold text-indigo-800">{fmtCur(groups.reduce((s, g) => s + g.total_gst, 0))} · {groups.length} invoice(s)</div>
          </div>
        </div>
      </Card>
      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50 sticky top-0">
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Creator / Cost</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Final cost</TableHead>
                <TableHead className="text-right">GST pending (18%)</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead><SortHeader label="Invoice date" sortKey="invoice_date" sort={sort} onToggle={toggleSort} testid="gst-sort-invoice-date" /></TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead><SortHeader label="Latest payment" sortKey="latest_paid_at" sort={sort} onToggle={toggleSort} testid="gst-sort-latest-payment" /></TableHead>
                {isAdmin && <TableHead className="text-right w-[160px]">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={cols} className="text-center text-slate-500 py-10">Loading…</TableCell></TableRow>
              ) : groups.length === 0 ? (
                <TableRow><TableCell colSpan={cols} className="text-center py-10 text-sm text-slate-500" data-testid="gst-empty">
                  {hasFilter ? `No GST reimbursements pending for ${fmtMonth(month)}.` : "No GST reimbursements pending."}
                </TableCell></TableRow>
              ) : groups.map((g) => (
                <Fragment key={g.key}>
                  <GroupRow g={g} isAdmin={isAdmin} open={!!openKeys[g.key]} onMark={setMarking}
                    onToggle={() => setOpenKeys((s) => ({ ...s, [g.key]: !s[g.key] }))} />
                  {openKeys[g.key] && g.rows.length > 1 && g.rows.map((r) => <ChildRow key={`${r.kind}-${r.id}`} r={r} isAdmin={isAdmin} />)}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
      <GstReimburseDialog group={marking} onOpenChange={(v) => !v && setMarking(null)} onSaved={() => { setMarking(null); onChanged?.(); }} />
    </div>
  );
}

function ProofUpload({ file, onFile, uploading }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500">Screenshot</Label>
      {file ? (
        <div className="mt-1 flex items-center justify-between gap-2 p-2 border border-emerald-200 bg-emerald-50 rounded-md text-xs" data-testid="gst-proof-selected">
          <span className="inline-flex items-center gap-1 truncate"><ImageIcon size={12} /> {file.name}</span>
          <button type="button" onClick={() => onFile(null)} className="text-slate-500 hover:text-rose-600" data-testid="gst-proof-remove"><X size={12} /></button>
        </div>
      ) : (
        <label className="mt-1 flex items-center justify-center gap-1.5 h-9 border border-dashed border-slate-300 rounded-md text-xs text-slate-600 cursor-pointer hover:bg-slate-50">
          <UploadSimple size={12} /> {uploading ? "Uploading…" : "Upload image or PDF"}
          <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden"
            onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid="gst-proof-input" />
        </label>
      )}
    </div>
  );
}

export function GstReimburseDialog({ group, onOpenChange, onSaved }) {
  const [date, setDate] = useState("");
  const [reference, setReference] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (group) { setDate(new Date().toISOString().slice(0, 10)); setReference(""); setFile(null); }
  }, [group]);
  const canSubmit = !!(reference.trim() || file);

  const submit = async () => {
    if (!group) return;
    if (!canSubmit) { toast.error("Add a reference number or upload a screenshot"); return; }
    setSaving(true);
    try {
      let proof_file_id = "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        proof_file_id = data.file_id;
      }
      await api.post("/accounting/gst-reimburse-bulk", {
        items: group.rows.map((r) => ({ kind: r.kind, id: r.id })),
        reimbursed_at: date, reference: reference.trim(), proof_file_id,
      });
      toast.success(`GST marked as reimbursed · ${group.rows.length} payment(s)`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const r = group?.first;
  return (
    <Dialog open={!!group} onOpenChange={onOpenChange}>
      <DialogContent data-testid="gst-reimburse-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-2xl">GST reimbursement · {r ? displayName(r) : ""}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="p-2 rounded-md bg-indigo-50 border border-indigo-200">
              <div className="text-[10px] uppercase tracking-wider text-indigo-700">Total GST</div>
              <div className="font-mono font-semibold text-indigo-800" data-testid="gst-dialog-total">{fmtCur(group?.total_gst)}</div>
            </div>
            <div className="p-2 rounded-md bg-slate-50 border border-slate-200">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Invoice #</div>
              <div className="font-mono font-semibold text-slate-900">{group?.invoice_number || "—"}</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Included payments ({group?.rows.length || 0})</div>
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="gst-dialog-items">
              {group?.rows.map((p) => (
                <div key={`${p.kind}-${p.id}`} className="flex items-center justify-between px-2 py-1.5 text-xs" data-testid={`gst-dialog-item-${p.id}`}>
                  <span className="text-slate-600">{kindLabel[p.kind]} · {fmtDate(p.latest_paid_at)} · {fmtCur(p.final_cost)}</span>
                  <span className="font-mono font-semibold text-indigo-700">{fmtCur(p.gst_pending)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Reimbursed on</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="gst-reimbursed-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Reference number</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / ARN" className="mt-1" data-testid="gst-reimbursed-ref" />
            </div>
          </div>
          <ProofUpload file={file} onFile={setFile} uploading={saving && !!file} />
          <div className={`text-[11px] ${canSubmit ? "text-emerald-700" : "text-amber-700"}`} data-testid="gst-proof-hint">
            {canSubmit ? "Proof added." : "Add a reference number or a screenshot (at least one is required)."}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !canSubmit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="gst-reimburse-save-btn">
            {saving ? "Saving…" : "Mark reimbursed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function GstHistorySection({ items, loading }) {
  const { openInvoice } = useInvoiceViewer();
  const [sort, toggleSort] = useSort("reimbursed");
  const rows = useMemo(() => sortRows(items, (h) => `${day(h.reimbursed_at)}|${h.marked_at || ""}`, sort.dir), [items, sort]);
  return (
    <div data-testid="gst-history-section">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Reimbursement history</div>
      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Creator / Cost</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead className="text-right">GST reimbursed</TableHead>
                <TableHead><SortHeader label="Reimbursed on" sortKey="reimbursed" sort={sort} onToggle={toggleSort} testid="gst-history-sort-date" /></TableHead>
                <TableHead>Reference #</TableHead>
                <TableHead>Screenshot</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-slate-500" data-testid="gst-history-empty">No reimbursements recorded yet.</TableCell></TableRow>
              ) : rows.map((h) => (
                <TableRow key={`${h.kind}-${h.id}`} data-testid={`gst-history-row-${h.id}`}>
                  <TableCell className="text-xs font-medium text-slate-900">
                    {displayName(h)}
                    <span className="ml-1.5 text-[10px] rounded-full px-1.5 py-0.5 font-semibold border bg-slate-50 text-slate-600 border-slate-200">{kindLabel[h.kind]}</span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-700">
                    <div className="font-semibold text-slate-900">{h.brand_name || "—"}</div>
                    {h.campaign_name && <div className="text-[11px] text-slate-500">{h.campaign_name}</div>}
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-slate-700">{h.invoice_number || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-emerald-700" data-testid={`gst-history-amount-${h.id}`}>{fmtCur(h.gst_pending)}</TableCell>
                  <TableCell className="text-xs text-slate-700 whitespace-nowrap" data-testid={`gst-history-date-${h.id}`}>{fmtDateTime(h.reimbursed_at, h.marked_at)}</TableCell>
                  <TableCell className="font-mono text-[11px] text-slate-700" data-testid={`gst-history-ref-${h.id}`}>{h.reimbursed_ref || "—"}</TableCell>
                  <TableCell>
                    {h.proof_file_id ? (
                      <button type="button" onClick={() => openInvoice(h.proof_file_id, "Reimbursement proof")}
                        className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 text-xs" data-testid={`gst-history-proof-${h.id}`}>
                        <ImageIcon size={11} /> View
                      </button>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
